/**
 * recurrence.ts — Pure RRULE subset parser + occurrence expander.
 *
 * Supported subset (D9):
 *   FREQ=DAILY|WEEKLY|MONTHLY
 *   INTERVAL=n  (default 1)
 *   BYDAY=MO,TU,WE,TH,FR,SA,SU  (weekly only)
 *   COUNT=n  OR  UNTIL=YYYYMMDD  (mutually exclusive)
 *
 * exdates[] filters occurrences by ISO date (YYYY-MM-DD).
 * Expansion is always range-bounded with a hard cap of MAX_OCCURRENCES.
 */

import type { CalendarEvent } from "../types";

// ------------------------------------------------------------------
// Types
// ------------------------------------------------------------------

export type RruleFreq = "DAILY" | "WEEKLY" | "MONTHLY";

/** Parsed representation of a supported RRULE string. */
export type ParsedRrule = {
  freq: RruleFreq;
  /** Step between occurrences (default: 1). */
  interval: number;
  /** Weekday codes for WEEKLY freq only ("MO","TU",…). */
  byday: string[];
  /** Max number of occurrences (mutually exclusive with until). */
  count?: number;
  /** Inclusive end date "YYYY-MM-DD" (mutually exclusive with count). */
  until?: string;
};

// Map abbreviated day codes to JS getDay() values (0=Sun…6=Sat)
const BYDAY_TO_JS: Record<string, number> = {
  SU: 0, MO: 1, TU: 2, WE: 3, TH: 4, FR: 5, SA: 6,
};

const MAX_OCCURRENCES = 1000;

// ------------------------------------------------------------------
// Helpers
// ------------------------------------------------------------------

/** Build "YYYY-MM-DD" from a local Date without timezone conversion. */
function toIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Parse "YYYY-MM-DD" to a local midnight Date. */
function fromIso(s: string): Date {
  return new Date(`${s}T00:00:00`);
}

/**
 * Advance a date by one DAILY step.
 * Returns a new Date; does not mutate.
 */
function stepDaily(d: Date, interval: number): Date {
  const next = new Date(d);
  next.setDate(next.getDate() + interval);
  return next;
}

/**
 * Advance a date by one MONTHLY step.
 * If the target month has fewer days than the original day-of-month,
 * the overflow skips that month entirely (e.g. Jan-31 + 1 month → Mar-31).
 */
function stepMonthly(d: Date, interval: number, originDay: number): Date {
  const next = new Date(d);
  next.setDate(1); // avoid overflow carry while changing month
  next.setMonth(next.getMonth() + interval);
  // Restore original day; setDate handles month-length automatically
  const daysInTarget = new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate();
  if (originDay > daysInTarget) {
    // Skip months that don't have this day (e.g. Feb-31 → skip Feb)
    next.setDate(1);
    next.setMonth(next.getMonth() + interval);
    const daysInFallback = new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate();
    next.setDate(Math.min(originDay, daysInFallback));
  } else {
    next.setDate(originDay);
  }
  return next;
}

// ------------------------------------------------------------------
// parseRrule
// ------------------------------------------------------------------

/**
 * Parse a simplified RRULE string into a structured object.
 * Returns null for empty/invalid strings.
 *
 * Example input: "FREQ=WEEKLY;INTERVAL=1;BYDAY=MO,WE,FR"
 */
export function parseRrule(rrule: string): ParsedRrule | null {
  if (!rrule || typeof rrule !== "string") return null;

  const parts: Record<string, string> = {};
  for (const segment of rrule.split(";")) {
    const eq = segment.indexOf("=");
    if (eq === -1) continue;
    parts[segment.slice(0, eq).trim().toUpperCase()] = segment.slice(eq + 1).trim();
  }

  const freq = parts["FREQ"] as RruleFreq | undefined;
  if (!freq || !["DAILY", "WEEKLY", "MONTHLY"].includes(freq)) return null;

  const interval = parts["INTERVAL"] ? Math.max(1, parseInt(parts["INTERVAL"], 10)) : 1;

  // BYDAY only meaningful for WEEKLY; ignore silently for other FREQ
  const byday: string[] = [];
  if (freq === "WEEKLY" && parts["BYDAY"]) {
    for (const code of parts["BYDAY"].split(",")) {
      const clean = code.trim().toUpperCase();
      if (clean in BYDAY_TO_JS) byday.push(clean);
    }
  }

  // COUNT and UNTIL are mutually exclusive; COUNT wins if both present
  let count: number | undefined;
  let until: string | undefined;
  if (parts["COUNT"]) {
    count = Math.max(1, parseInt(parts["COUNT"], 10));
  } else if (parts["UNTIL"]) {
    // Normalize "YYYYMMDD" or "YYYY-MM-DD" to "YYYY-MM-DD"
    const raw = parts["UNTIL"].replace(/[^0-9]/g, "");
    if (raw.length === 8) {
      until = `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
    }
  }

  return { freq, interval, byday, count, until };
}

// ------------------------------------------------------------------
// expandRecurrence
// ------------------------------------------------------------------

/**
 * Expand a CalendarEvent into occurrence date strings within [rangeStart, rangeEnd].
 *
 * - Events without an rrule return [event.date] if the date falls within the range.
 * - Respects exdates[] by filtering those dates from the result.
 * - Hard-capped at MAX_OCCURRENCES to prevent infinite loops.
 *
 * @param event        CalendarEvent (must have .date as ISO "YYYY-MM-DD")
 * @param rangeStartIso  Range start inclusive "YYYY-MM-DD"
 * @param rangeEndIso    Range end inclusive "YYYY-MM-DD"
 * @returns Array of "YYYY-MM-DD" strings within the range (exdates excluded)
 */
export function expandRecurrence(
  event: CalendarEvent,
  rangeStartIso: string,
  rangeEndIso: string,
): string[] {
  const rangeEnd = fromIso(rangeEndIso);
  const exdateSet = new Set<string>(event.exdates ?? []);

  // Non-recurring event: simple inclusion check
  if (!event.rrule) {
    if (event.date >= rangeStartIso && event.date <= rangeEndIso && !exdateSet.has(event.date)) {
      return [event.date];
    }
    return [];
  }

  const rule = parseRrule(event.rrule);
  if (!rule) {
    // Unparseable rrule → treat as single event
    if (event.date >= rangeStartIso && event.date <= rangeEndIso && !exdateSet.has(event.date)) {
      return [event.date];
    }
    return [];
  }

  const originDay = fromIso(event.date).getDate(); // used for monthly stepping
  const untilDate = rule.until ? fromIso(rule.until) : null;

  const results: string[] = [];
  let current = fromIso(event.date);
  let totalGenerated = 0; // tracks how many occurrences generated overall (for COUNT)

  while (true) {
    // Hard cap: safety against runaway loops
    if (totalGenerated >= MAX_OCCURRENCES) break;

    const currentIso = toIso(current);

    // Stop if we've exceeded UNTIL
    if (untilDate && current > untilDate) break;

    // Stop if we've exceeded COUNT
    if (rule.count !== undefined && totalGenerated >= rule.count) break;

    // Stop if current is past the range end (no more occurrences can match)
    if (current > rangeEnd) break;

    // Determine if this step produces occurrences
    if (rule.freq === "WEEKLY" && rule.byday.length > 0) {
      // Each "step" covers the calendar week (Mon–Sun) that contains `current`.
      // Always anchor to that Monday so that each calendar date is visited
      // exactly once — no start-anchored first window that overlaps the next
      // Monday-anchored window (which was the source of duplicates for
      // mid-week series starts).
      const weekStartDay = current.getDay();
      const daysToMonday = (weekStartDay + 6) % 7; // days since Monday (0 on Mon)
      const thisMonday = new Date(current);
      thisMonday.setDate(current.getDate() - daysToMonday);

      // Iterate Mon..Sun of this calendar week
      for (let offset = 0; offset < 7; offset++) {
        if (totalGenerated >= MAX_OCCURRENCES) break;
        if (rule.count !== undefined && totalGenerated >= rule.count) break;

        const candidate = new Date(thisMonday);
        candidate.setDate(thisMonday.getDate() + offset);
        const candidateIso = toIso(candidate);

        // Only days matching BYDAY codes
        const dayJs = candidate.getDay();
        const dayCode = Object.entries(BYDAY_TO_JS).find(([, v]) => v === dayJs)?.[0];
        if (!dayCode || !rule.byday.includes(dayCode)) continue;

        // BYDAY days must be >= event.date (series starts on event.date)
        if (candidateIso < event.date) continue;

        totalGenerated++;

        if (untilDate && candidate > untilDate) continue;
        if (candidate > rangeEnd) continue;
        if (candidateIso < rangeStartIso) continue;
        if (exdateSet.has(candidateIso)) continue;

        results.push(candidateIso);
      }

      // Advance to next week cycle: thisMonday + interval*7 days
      const nextMonday = new Date(thisMonday);
      nextMonday.setDate(thisMonday.getDate() + rule.interval * 7);
      current = nextMonday;

      // Stop early when we've already passed the range end and COUNT isn't still driving us
      if (current > rangeEnd && (rule.count === undefined || totalGenerated >= rule.count)) break;

    } else {
      // DAILY or MONTHLY (or WEEKLY without BYDAY)
      // Series starts on event.date; only count occurrences from that date
      if (currentIso >= event.date) {
        totalGenerated++;

        if (currentIso >= rangeStartIso && !exdateSet.has(currentIso)) {
          results.push(currentIso);
        }
      }

      // Advance to next occurrence
      if (rule.freq === "DAILY") {
        current = stepDaily(current, rule.interval);
      } else if (rule.freq === "MONTHLY") {
        current = stepMonthly(current, rule.interval, originDay);
      } else {
        // WEEKLY without BYDAY: step by interval weeks
        current = stepDaily(current, rule.interval * 7);
      }
    }
  }

  return results;
}
